import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { searchAll, SECTION_LABELS, pieces, recentSearches, rememberSearch, forgetSearches } from '../lib/search';
import { cameFrom } from './asset/BackLink';
import './GlobalSearch.css';

/**
 * THE GLOBAL SEARCH (Gordon, 3 November): one box in the top bar - and in
 * the phone's menu - and Ctrl+K / Cmd+K from anywhere, opening a command
 * palette: type, and the results come back grouped (Tickets, Assets, People,
 * Help & docs, Links, To-dos, Chat, and for administrators Licences,
 * Contracts and Settings), a few of each, with "See all in <area>" opening
 * that area's own page with the same words.
 *
 * WHAT IS SHOWN IS THE SERVER'S WORD (backend lib/search-sections.js): each
 * section asks its own area's permission rules, so nothing here hides
 * anything - a section that is not in the answer is not drawn.
 *
 * ONE REQUEST PER PAUSE IN TYPING (200ms), and only the latest answer is
 * drawn: each request is numbered and the one before it cancelled, so a slow
 * answer to "pri" never lands on top of the answer to "printer" (the
 * licences lesson). A section that ran out of time is shown as "Still
 * searching…" and asked for again on its own, with longer to answer.
 *
 * SNIPPETS are plain text with the matches as ranges; they are drawn as text
 * nodes with <mark> around the ranges, so whatever anybody typed is shown as
 * the characters it is.
 *
 * KEYBOARD: ↑ ↓ move through every result and "See all" row, Enter opens,
 * Esc closes; the focus stays in the box. Recent searches (this person's, in
 * this browser only - lib/search.js) are offered while the box is empty.
 */

const DEBOUNCE_MS = 200;
const OPEN_EVENT = 'jarvis:open-search';

/** A later answer for some sections, put in place of theirs. */
const merge = (base, extra) => {
  if (!base) return extra;
  const by = new Map((extra.sections || []).map((s) => [s.key, s]));
  return { ...base, sections: base.sections.map((s) => by.get(s.key) || s) };
};

/**
 * Open the search from anywhere: the bar's box, the phone menu's row. With
 * words, it opens with them already asked - "← Search results" on a device
 * opened from here (components/asset/BackLink.js).
 */
export const openSearch = (words) => window.dispatchEvent(new CustomEvent(OPEN_EVENT,
  { detail: typeof words === 'string' ? { q: words } : null }));

/** Ctrl+K on Windows and Linux, ⌘K on a Mac - for the hint beside the box. */
export const shortcutLabel = () => {
  try {
    return /Mac|iPhone|iPad/i.test(navigator.platform || navigator.userAgent || '') ? '⌘K' : 'Ctrl K';
  } catch { return 'Ctrl K'; }
};

const SearchGlyph = ({ size = 18 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
    <circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" />
  </svg>
);

/** The box in the bar (and the phone menu's row): a button that opens the palette. */
export function SearchButton({ inSheet = false }) {
  return (
    <button type="button" className={`gs-open${inSheet ? ' is-sheet' : ''}`} onClick={openSearch}
      aria-label="Search Jarvis" aria-keyshortcuts="Control+K Meta+K" title={`Search (${shortcutLabel()})`}>
      <SearchGlyph size={16} />
      <span className="gs-open-word">Search</span>
      {!inSheet && <kbd className="gs-open-kbd" aria-hidden="true">{shortcutLabel()}</kbd>}
    </button>
  );
}

/** Text with its ranges marked. */
function Marked({ text, ranges }) {
  return pieces(text, ranges).map((p, i) => (p.hit
    // eslint-disable-next-line react/no-array-index-key
    ? <mark key={i} className="gs-hit">{p.text}</mark>
    // eslint-disable-next-line react/no-array-index-key
    : <React.Fragment key={i}>{p.text}</React.Fragment>));
}

/**
 * The palette. `who` names the person searching, for their recent searches;
 * `isAdmin` lets it say when the server's indexes are missing.
 */
export default function GlobalSearch({ who, isAdmin = false }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [answer, setAnswer] = useState(null);       // { q, sections, indexed }
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState(null);
  const [at, setAt] = useState(0);
  const [recent, setRecent] = useState([]);
  const inputRef = useRef(null);
  const panelRef = useRef(null);
  const listRef = useRef(null);
  const returnTo = useRef(null);
  const seq = useRef(0);
  const ctrl = useRef(null);

  /* ---- opening and closing */

  const show = useCallback((e) => {
    returnTo.current = document.activeElement;
    // A fresh box each time, with this person's recent searches under it -
    // or the words it was opened with.
    setQ(typeof e?.detail?.q === 'string' ? e.detail.q : '');
    setAnswer(null);
    setProblem(null);
    setRecent(recentSearches(who));
    setOpen(true);
  }, [who]);

  const hide = useCallback(() => {
    setOpen(false);
    if (ctrl.current) ctrl.current.abort();
    seq.current += 1;
    const back = returnTo.current;
    if (back && typeof back.focus === 'function' && document.contains(back)) {
      setTimeout(() => back.focus({ preventScroll: true }), 0);
    }
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      // A page that uses Ctrl+K itself keeps it: the docs editor's "add a
      // link", and To do's own command palette (which stops the key going
      // further). Everywhere else it is this.
      if (e.defaultPrevented || e.isComposing) return;
      if ((e.ctrlKey || e.metaKey) && !e.altKey && !e.shiftKey && String(e.key).toLowerCase() === 'k') {
        e.preventDefault();
        if (open) inputRef.current?.select();
        else show();
      }
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener(OPEN_EVENT, show);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener(OPEN_EVENT, show);
    };
  }, [open, show]);

  // The page behind does not scroll while it is open, and is put back as it was.
  useEffect(() => {
    if (!open) return undefined;
    const had = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    setTimeout(() => inputRef.current?.focus(), 0);
    return () => { document.body.style.overflow = had; };
  }, [open]);

  // Letters typed while the palette is opening - before the box has the
  // focus - went to the page and were lost ("printer" searched as "rinter"
  // on a slow machine). Until the box has the focus, they are caught here and
  // added to it, and the box is given the focus.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      const box = inputRef.current;
      if (!box || document.activeElement === box) return;
      if (e.ctrlKey || e.metaKey || e.altKey || e.isComposing || String(e.key).length !== 1) return;
      e.preventDefault();
      setQ((was) => was + e.key);
      box.focus();
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [open]);

  // Somebody else is searching now (sign-in, View as): start afresh.
  useEffect(() => { setQ(''); setAnswer(null); setRecent(recentSearches(who)); }, [who]);

  /* ---- asking */

  useEffect(() => {
    if (!open) return undefined;
    const words = q.trim();
    if (!words) {
      if (ctrl.current) ctrl.current.abort();
      seq.current += 1;
      setAnswer(null); setBusy(false); setProblem(null); setAt(0);
      return undefined;
    }
    const timer = setTimeout(async () => {
      if (ctrl.current) ctrl.current.abort();
      const mine = ++seq.current;
      const c = new AbortController();
      ctrl.current = c;
      setBusy(true);
      try {
        const got = await searchAll(words, { signal: c.signal });
        if (mine !== seq.current) return;          // a newer question is out: this answer is old
        setAnswer(got); setProblem(null); setAt(0); setBusy(false);
        // Still searching: ask those sections again, on their own, with longer.
        const slow = got.sections.filter((s) => s.status === 'timeout').map((s) => s.key);
        if (slow.length) {
          const later = await searchAll(words, { sections: slow, wait: 'long', signal: c.signal });
          if (mine !== seq.current) return;
          setAnswer((was) => merge(was, later));
        }
      } catch (error) {
        if (mine !== seq.current || error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED') return;
        setBusy(false);
        setProblem(error?.response?.data?.error || 'Search is not answering just now. Try again in a moment.');
      }
    }, DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [q, open]);

  /** "Show more" for a section with no page of its own: ask it alone, for more. */
  const more = async (key) => {
    const words = q.trim();
    const mine = seq.current;
    try {
      const got = await searchAll(words, { sections: [key], limit: 20 });
      if (mine !== seq.current) return;
      setAnswer((was) => merge(was, { sections: got.sections.map((s) => ({ ...s, expanded: true })) }));
    } catch { /* the rows already shown stay */ }
  };

  /* ---- what is drawn, as one list of rows for the keyboard */

  const groups = useMemo(() => {
    if (!answer) return [];
    return answer.sections
      .filter((s) => s.items.length || s.status === 'timeout' || s.status === 'failed')
      .map((s) => ({ ...s, label: s.label || SECTION_LABELS[s.key] || s.key }));
  }, [answer]);

  const rows = useMemo(() => {
    const list = [];
    if (!q.trim()) {
      recent.forEach((r) => list.push({ kind: 'recent', id: `recent-${r}`, text: r }));
      return list;
    }
    for (const g of groups) {
      g.items.forEach((it) => list.push({ kind: 'item', id: `${g.key}-${it.id}`, group: g.key, item: it }));
      if (g.see_all) list.push({ kind: 'all', id: `${g.key}-all`, group: g.key, link: g.see_all, label: g.label });
      else if (g.more && !g.expanded) list.push({ kind: 'more', id: `${g.key}-more`, group: g.key, label: g.label });
    }
    return list;
  }, [groups, recent, q]);

  useEffect(() => { if (at >= rows.length) setAt(Math.max(0, rows.length - 1)); }, [rows, at]);

  // The row the keyboard is on stays in sight.
  useEffect(() => {
    const el = listRef.current?.querySelector('[data-active="true"]');
    if (el && typeof el.scrollIntoView === 'function') el.scrollIntoView({ block: 'nearest' });
  }, [at, rows]);

  const go = (row) => {
    if (!row) return;
    if (row.kind === 'recent') { setQ(row.text); return; }
    if (row.kind === 'more') { more(row.group); return; }
    rememberSearch(who, q);
    const ext = row.kind === 'item' ? row.item.external : null;
    hide();
    if (ext) window.open(ext, '_blank', 'noopener,noreferrer');
    else if (row.kind === 'item' && /^\/assets\/[^/]+$/.test(row.item.link || '')) {
      // A device: its "←" says "Search results" and reopens this search.
      navigate(row.item.link, { state: cameFrom('Search results', { search: q.trim() }) });
    } else navigate(row.kind === 'all' ? row.link : row.item.link);
  };

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setAt((i) => (rows.length ? (i + 1) % rows.length : 0)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setAt((i) => (rows.length ? (i - 1 + rows.length) % rows.length : 0)); }
    else if (e.key === 'Home' && rows.length && !q) { e.preventDefault(); setAt(0); }
    else if (e.key === 'Enter') { if (rows[at]) { e.preventDefault(); go(rows[at]); } }
    else if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); hide(); }
    else if (e.key === 'Tab') {
      // Focus stays in the palette: the box, and the one or two buttons in it.
      const items = Array.from(panelRef.current?.querySelectorAll('input, button:not([disabled])') || []);
      if (!items.length) return;
      const i = items.indexOf(document.activeElement);
      const next = e.shiftKey ? (i <= 0 ? items.length - 1 : i - 1) : (i === items.length - 1 ? 0 : i + 1);
      e.preventDefault();
      items[next].focus();
    }
  };

  if (!open) return null;

  const listId = 'gs-results';
  const optionId = (i) => `gs-opt-${i}`;
  const nothing = q.trim() && answer && !busy && groups.length === 0;
  let n = -1;
  const rowProps = (row) => {
    n += 1;
    const i = n;
    return {
      id: optionId(i), role: 'option', 'aria-selected': i === at, 'data-active': i === at ? 'true' : undefined,
      className: `gs-row is-${row.kind}${i === at ? ' is-active' : ''}`,
      onMouseMove: () => { if (at !== i) setAt(i); },
      onMouseDown: (e) => e.preventDefault(),
      onClick: () => go(row)
    };
  };

  return (
    <div className="gs-scrim" onMouseDown={(e) => { if (e.target === e.currentTarget) hide(); }}>
      <div className="gs-panel" ref={panelRef} role="dialog" aria-modal="true" aria-label="Search Jarvis"
        onKeyDown={onKeyDown}>
        <div className="gs-head">
          <SearchGlyph />
          <label htmlFor="gs-q" className="gs-sr">Search Jarvis</label>
          <input
            id="gs-q"
            ref={inputRef}
            className="gs-input"
            type="search"
            autoComplete="off"
            spellCheck="false"
            placeholder="Search tickets, devices, people, docs…"
            role="combobox"
            aria-expanded={rows.length > 0}
            aria-controls={listId}
            aria-autocomplete="list"
            aria-activedescendant={rows[at] ? optionId(at) : undefined}
            value={q}
            maxLength={120}
            onChange={(e) => setQ(e.target.value)}
          />
          {busy && <span className="gs-busy" role="status" aria-label="Searching" />}
          <button type="button" className="gs-close" onClick={hide} aria-label="Close search">
            <span aria-hidden="true">Esc</span>
          </button>
        </div>

        <div className="gs-body" ref={listRef}>
          <p className="gs-sr" role="status" aria-live="polite">
            {nothing ? 'Nothing found' : answer && q.trim() ? `${rows.filter((r) => r.kind === 'item').length} results` : ''}
          </p>
          {problem && <p className="gs-note is-problem">{problem}</p>}

          {!q.trim() && (
            recent.length ? (
              <div className="gs-group" role="listbox" id={listId} aria-label="Recent searches">
                <div className="gs-group-head">
                  <span>Recent searches</span>
                  <button type="button" className="gs-link" onClick={() => { forgetSearches(who); setRecent([]); }}>Clear</button>
                </div>
                {rows.map((row) => (
                  <div key={row.id} {...rowProps(row)}>
                    <span className="gs-row-title">{row.text}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="gs-note">Type to search tickets, devices, people, docs, links, to-dos and chats.</p>
            )
          )}

          {q.trim() && (
            <div role="listbox" id={listId} aria-label="Results">
              {groups.map((g) => (
                <div className="gs-group" key={g.key} role="group" aria-labelledby={`gs-g-${g.key}`} data-section={g.key}>
                  <div className="gs-group-head"><span id={`gs-g-${g.key}`}>{g.label}</span></div>
                  {g.status === 'timeout' && <p className="gs-note is-small">Still searching…</p>}
                  {g.status === 'failed' && <p className="gs-note is-small is-problem">{g.label} could not be searched just now.</p>}
                  {g.items.map((it) => {
                    const row = rows.find((r) => r.kind === 'item' && r.group === g.key && r.item === it);
                    return (
                      <div key={it.id} {...rowProps(row)} data-type={it.type}>
                        <span className="gs-row-title"><Marked text={it.title} ranges={it.title_highlights} /></span>
                        {it.subtitle && <span className="gs-row-sub">{it.subtitle}</span>}
                        {it.snippet && <span className="gs-row-snip"><Marked text={it.snippet} ranges={it.highlights} /></span>}
                      </div>
                    );
                  })}
                  {rows.filter((r) => r.group === g.key && (r.kind === 'all' || r.kind === 'more')).map((row) => (
                    <div key={row.id} {...rowProps(row)}>
                      <span className="gs-row-title">{row.kind === 'all' ? `See all in ${row.label}` : `Show more ${row.label.toLowerCase()}`}</span>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}

          {nothing && <p className="gs-note">Nothing found for “{q.trim()}”.</p>}
        </div>

        <div className="gs-foot">
          <span className="gs-keys" aria-hidden="true"><kbd>↑</kbd><kbd>↓</kbd> to move · <kbd>Enter</kbd> to open · <kbd>Esc</kbd> to close</span>
          {isAdmin && answer?.indexed === false && (
            <span className="gs-index-note">Search is not indexed on this server yet - run migrate:126 to make it faster.</span>
          )}
        </div>
      </div>
    </div>
  );
}
